import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Snackbar,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const statusColors = {
    pending: 'default',
    processing: 'info',
    completed: 'success',
    failed: 'error',
};

const getCsrfToken = () => {
    if (window.csrfToken) {
        return window.csrfToken;
    }

    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
};

const normalizeDateValue = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }
    const hasExplicitTimezone = /(?:Z|[+\-]\d{2}:?\d{2})$/i.test(trimmed);
    if (hasExplicitTimezone) {
        return trimmed;
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(trimmed)) {
        return trimmed;
    }
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toISOString();
};

const buildItemLabel = (item) => {
    if (!item) {
        return '';
    }
    const groupLabel = item.group ? ` • ${item.group}` : '';
    return `#${item.id} · ${item.name}${groupLabel}`;
};

const AlarmCleanupDrawer = ({
    items = [],
    storeUrl = '',
    indexUrl = '',
    ownerName = '',
}) => {
    const [open, setOpen] = useState(false);
    const [pageFilters, setPageFilters] = useState({
        startAt: '',
        endAt: '',
        itemId: null,
        groupName: null,
        readStatus: '',
        ruleName: '',
        tag: '',
        totalMatches: null,
    });
    const [submitting, setSubmitting] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [batches, setBatches] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [formError, setFormError] = useState('');
    const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
    const dateTimeFormatter = useMemo(() => new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }), []);

    const selectedFluxLabel = useMemo(() => {
        if (!pageFilters.itemId) {
            return null;
        }
        const match = items.find((item) => item.id === pageFilters.itemId);
        return match ? buildItemLabel(match) : `Flux ID ${pageFilters.itemId}`;
    }, [items, pageFilters.itemId]);

    const fetchBatches = useCallback(async () => {
        if (!indexUrl) {
            return;
        }
        setStatusLoading(true);
        try {
            const response = await fetch(indexUrl, {
                headers: {
                    'Accept': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error('Unable to load cleanup batches.');
            }
            const payload = await response.json();
            setBatches(Array.isArray(payload.data) ? payload.data : []);
        } catch (error) {
            console.warn('Failed to load alarm cleanup batches', error);
        } finally {
            setStatusLoading(false);
        }
    }, [indexUrl]);

    useEffect(() => {
        fetchBatches();
    }, [fetchBatches]);

    useEffect(() => {
        const handleStateSync = (event) => {
            const detail = event.detail || {};
            const parsedItem = typeof detail.itemId === 'number'
                ? detail.itemId
                : Number.parseInt(detail.itemId, 10);
            setPageFilters({
                startAt: detail.startAt || '',
                endAt: detail.endAt || '',
                itemId: Number.isFinite(parsedItem) ? parsedItem : null,
                groupName: detail.groupName || null,
                readStatus: detail.readStatus || '',
                ruleName: detail.ruleName || '',
                tag: detail.tag || '',
                totalMatches: typeof detail.totalMatches === 'number' ? detail.totalMatches : null,
            });
        };

        window.addEventListener('alarmCleanup:filtersSync', handleStateSync);
        if (window.alarmCleanupState) {
            handleStateSync({ detail: window.alarmCleanupState });
        }

        return () => {
            window.removeEventListener('alarmCleanup:filtersSync', handleStateSync);
        };
    }, []);

    const handleClose = () => {
        setOpen(false);
        setFormError('');
    };

    const handleOpen = () => {
        setOpen(true);
        setFormError('');
    };

    const handleQueue = async () => {
        const hasStart = Boolean(pageFilters.startAt);
        const hasEnd = Boolean(pageFilters.endAt);

        if (!hasStart || !hasEnd) {
            setFormError('Set both start and end timestamps on the alerts page first.');
            return;
        }

        if (pageFilters.itemId && pageFilters.groupName) {
            setFormError('Select either a single flux or a group, not both.');
            return;
        }

        const applyAll = !pageFilters.itemId && !pageFilters.groupName;

        setSubmitting(true);
        setFormError('');
        try {
            const payload = {
                start_at: normalizeDateValue(pageFilters.startAt),
                end_at: normalizeDateValue(pageFilters.endAt),
                item_ids: applyAll || !pageFilters.itemId ? [] : [pageFilters.itemId],
                group_names: applyAll || !pageFilters.groupName ? [] : [pageFilters.groupName],
                apply_all: applyAll,
                read_status: pageFilters.readStatus || null,
                rule_name: pageFilters.ruleName || null,
                tag: pageFilters.tag || null,
                notes: null,
            };

            const response = await fetch(storeUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorBody = await response.json().catch(() => ({}));
                const message = errorBody.message || 'Unable to queue cleanup batch.';
                throw new Error(message);
            }

            const body = await response.json();
            setSnackbar({ open: true, message: body.message || 'Cleanup batch queued.', severity: 'success' });
            fetchBatches();
            setOpen(false);
        } catch (error) {
            setSnackbar({ open: true, message: error.message || 'Failed to queue cleanup batch.', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRefresh = useCallback(async () => {
        await fetchBatches();
        window.dispatchEvent(new CustomEvent('alarmCleanup:forceReload'));
    }, [fetchBatches]);

    const statusList = useMemo(() => batches.slice(0, 10), [batches]);

    const summarizeDateTime = (value) => {
        if (!value) {
            return 'Not set';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return 'Not set';
        }
        return dateTimeFormatter.format(date);
    };

    const describeReadStatus = (value) => {
        if (value === 'read') {
            return 'Read alarms only';
        }
        if (value === 'unread') {
            return 'Unread alarms only';
        }
        return 'All alarms';
    };

    const formattedMatches = typeof pageFilters.totalMatches === 'number'
        ? numberFormatter.format(pageFilters.totalMatches)
        : '—';

    return (
        <Box sx={{mb: 2}}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                <Box>
                    <Typography variant="h6">Admin Cleanup Tools</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Impersonating: <strong>{ownerName}</strong>. Queue background deletions without touching incident alarms.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={handleOpen}>
                        Configure Cleanup
                    </Button>
                </Stack>
            </Stack>

            <Drawer anchor="right" open={open} onClose={handleClose} sx={{ zIndex: 1300 }}>
                <Box sx={{ width: { xs: '100%', sm: 600 }, p: 3 }} role="presentation">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Bulk Alarm Deletion</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This action deletes alarms, screenshots, videos, and labels that match the filters below. Incidents (issue=true) are always preserved.
                    </Typography>

                    <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Current selection from alerts table
                        </Typography>
                        <Typography variant="body2">Start: {summarizeDateTime(pageFilters.startAt)}</Typography>
                        <Typography variant="body2">End: {summarizeDateTime(pageFilters.endAt)}</Typography>
                        <Typography variant="body2">Flux scope: {selectedFluxLabel || 'All accessible fluxes'}</Typography>
                        <Typography variant="body2">Group scope: {pageFilters.groupName || 'All groups'}</Typography>
                        <Typography variant="body2">Read filter: {describeReadStatus(pageFilters.readStatus)}</Typography>
                        {pageFilters.ruleName && <Typography variant="body2">Rule name: <strong>{pageFilters.ruleName}</strong></Typography>}
                        {pageFilters.tag && <Typography variant="body2">Tag: <strong>{pageFilters.tag}</strong></Typography>}
                        <Typography variant="body2">Matching alerts in preview: {formattedMatches}</Typography>
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Confirm the alerts table preview matches this selection before queuing deletions. Incidents remain protected automatically.
                        </Alert>
                    </Box>

                    <Stack spacing={2} sx={{ mt: 3 }}>
                        <Alert severity="info">
                            Adjust filters, time range, and read status directly on the alerts page. This drawer now mirrors that selection.
                        </Alert>
                        {(!pageFilters.startAt || !pageFilters.endAt) && (
                            <Alert severity="warning">
                                Choose both start and end times before queueing a cleanup.
                            </Alert>
                        )}
                        {formError && (
                            <Alert severity="error" onClose={() => setFormError('')}>
                                {formError}
                            </Alert>
                        )}
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleQueue}
                            disabled={submitting || !pageFilters.startAt || !pageFilters.endAt}
                        >
                            {submitting ? <CircularProgress size={20} /> : 'Queue Deletion for This Selection'}
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="subtitle1">Recent Batches</Typography>
                        {statusLoading && <CircularProgress size={16} />}
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleRefresh}
                            disabled={statusLoading}
                        >
                            Refresh
                        </Button>
                    </Stack>

                    {statusList.length === 0 && !statusLoading && (
                        <Typography variant="body2" color="text.secondary">
                            No cleanup batches queued yet.
                        </Typography>
                    )}

                    <Stack spacing={1}>
                        {statusList.map((batch) => (
                            <Box key={batch.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                    <Typography variant="subtitle2">Batch #{batch.id}</Typography>
                                    <Chip label={batch.status} color={statusColors[batch.status] || 'default'} size="small" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Deleted {batch.deleted_alarms} / {batch.total_matched} alarms · Skipped {batch.skipped_incidents} incidents
                                </Typography>
                                {batch.error_message && (
                                    <Typography variant="caption" color="error">
                                        {batch.error_message}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    Started: {batch.processing_started_at || 'pending'} · Finished: {batch.processing_finished_at || 'pending'}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Drawer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AlarmCleanupDrawer;
